import { default as React } from 'react';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  helpIcon: {
    padding: 2,
    marginLeft: 4,
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 4,
  },
  tooltipText: {
    fontSize: '0.875rem',
  },
  tooltipLink: {
    color: theme.palette.primary.light,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

interface DocsHelpIconProps {
  docsUrl: string;
  tooltipText?: string;
}

/**
 * A help icon that displays a tooltip with a link to documentation.
 * Clicking the icon opens the documentation in a new tab.
 */
export const DocsHelpIcon: React.FC<DocsHelpIconProps> = ({
  docsUrl,
  tooltipText = 'Click for documentation',
}) => {
  const classes = useStyles();

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(docsUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip
      title={
        <div className={classes.tooltipContent}>
          <span className={classes.tooltipText}>{tooltipText}</span>
          <span className={classes.tooltipLink}>Learn more</span>
        </div>
      }
      arrow
      placement="top"
    >
      <IconButton
        className={classes.helpIcon}
        size="small"
        onClick={handleClick}
        aria-label="Help documentation"
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default DocsHelpIcon;
